// /plan/scenarios — Library of Workbench scenarios.
//
// Replaces the retired 7-category preset catalogue page. The
// per-asset libraries (/plan/airframes, /plan/threats, …) already
// cover the individual pieces, so this page now focuses on the
// scenario document itself: open / clone / delete / new.
//
// Data source: GET /api/sim/scenarios?scope=tenant|preset|mine.
// Default scope "tenant" returns the caller's own scenarios plus
// every is_preset row so new tenants always see the flagship demo.
//
// Columns:  Name · Owner/PRESET · Validation · Attached chips · Updated.
// Actions:  Open in Workbench (primary) · Clone · Delete.
// Header:   New scenario button → creates a blank sim_scenario and
//           navigates straight to /workbench/{id}.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Filter, Copy, Trash2, PlayCircle, FlaskConical, Globe, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatApiError } from "@/lib/formatApiError";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SpinnerButton from "@/components/SpinnerButton";
import PresetPublishButton from "@/components/PresetPublishButton";
import { sim } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthContext";
import { presetImmutabilityToast } from "@/lib/presetImmutability";

const SCOPES = [
    { id: "tenant", label: "All sources" },
    { id: "mine", label: "Mine" },
    { id: "preset", label: "Presets" },
];

const VALIDATIONS = [
    { id: "", label: "All validation" },
    { id: "runnable", label: "Runnable" },
    { id: "blocked", label: "Blocked" },
    { id: "draft", label: "Draft" },
];

// Phase 20 CP3.b — Customer-demo archetype factory presets. The IDs are
// the path-param Literal accepted by ``POST /api/sim/scenarios/from-
// archetype/{archetype_id}`` (see backend/routers/sim_scenarios.py);
// labels are UK-English display strings shown in the dropdown.
const CUSTOMER_ARCHETYPES = [
    { id: "swarm", label: "Mass swarm raid (25 inbound)" },
    { id: "high_speed_ingress", label: "High-speed ingress" },
    { id: "mixed_raid", label: "Mixed raid" },
    { id: "degraded_environment", label: "Degraded environment" },
];

// --------------------------------------------------------------------
// Small visual primitives — match the airframes/threats/env library
// patterns. Kept inline because they aren't reused elsewhere.
// --------------------------------------------------------------------
function OwnerBadge({ scenario }) {
    if (scenario?.is_preset || scenario?.summary?.is_preset) {
        return (
            <span
                className="inline-flex items-center border rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-ux-accent/10 text-ux-accent border-ux-accent/30"
                data-testid={`scenario-owner-${scenario.id}`}
            >
                PRESET
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center border rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-ux-bg-2 text-ux-text-2 border-ux-border-1"
            data-testid={`scenario-owner-${scenario.id}`}
        >
            {scenario?.owner_username || "—"}
        </span>
    );
}

// Multi-agent surface badge — Phase 20 CP2. A scenario is "multi-agent"
// when it carries any of the Phase 19 additive fields:
//   * ``cooperative_mode`` is set (independent / cued / netted), OR
//   * one or more ``interceptor_batteries``, OR
//   * one or more ``radars``, OR
//   * one or more ``threat_instances``.
// The first match wins.
function isMultiAgentScenario(scenario) {
    if (!scenario) return false;
    if (scenario.cooperative_mode) return true;
    if (Array.isArray(scenario.interceptor_batteries)
        && scenario.interceptor_batteries.length > 0) return true;
    if (Array.isArray(scenario.radars) && scenario.radars.length > 0) return true;
    if (Array.isArray(scenario.threat_instances)
        && scenario.threat_instances.length > 0) return true;
    return false;
}

function MultiAgentBadge({ scenario }) {
    if (!isMultiAgentScenario(scenario)) return null;
    return (
        <span
            className="inline-flex items-center border rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-ux-accent-2/10 text-ux-accent-2 border-ux-accent-2/30"
            style={{
                background: "rgba(79, 182, 198, 0.10)",
                color: "#4FB6C6",
                borderColor: "rgba(79, 182, 198, 0.35)",
            }}
            data-testid={`scenario-multiagent-${scenario.id}`}
            title="Multi-agent scenario — open in the battlespace editor"
        >
            Multi-agent
        </span>
    );
}

function ValidationPill({ scenario }) {
    // Heuristic off the list row — no summary from the server: treat
    // a scenario with a fully populated attachment set as "runnable",
    // a partially populated one as "draft". Anything explicitly flagged
    // with blocking issues via the row's validation object shows blocked.
    const s = scenario?.summary || {};
    const blocking = scenario?.validation?.blocking_count ?? 0;
    let label = "draft";
    let cls = "bg-ux-bg-2 text-ux-text-3 border-ux-border-1";
    if (blocking > 0) {
        label = `${blocking} blocking`;
        cls = "bg-ux-danger/10 text-ux-danger border-ux-danger/30";
    } else if (
        // CP-G9 P1-9 — derive the airframe count from ``airframe_ids[]``
        // on the live response; the legacy ``airframe_count`` summary
        // field was never persisted by the backend and would crash the
        // ErrorBoundary on the first iteration.
        (scenario?.airframe_ids?.length ?? 0) > 0 &&
        s.has_environment &&
        s.threat_count > 0 &&
        s.has_guidance_profile &&
        s.has_engagement
    ) {
        label = "runnable";
        cls = "bg-ux-success/10 text-ux-success border-ux-success/30";
    }
    return (
        <span
            className={`inline-flex items-center border rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${cls}`}
            data-testid={`scenario-validation-${scenario.id}`}
        >
            {label}
        </span>
    );
}

function MultiRadarPanel({ scenario }) {
    // CP-G8 (P2-2b) — surfaces multi-radar coverage when a scenario
    // carries ≥ 2 radars. Single-radar scenarios skip the panel
    // entirely to avoid clutter.
    const radars = Array.isArray(scenario?.radars) ? scenario.radars : [];
    if (radars.length < 2) return null;
    return (
        <div
            className="rounded-md border border-ux-border-1 bg-[#0B2545] text-white px-3 py-2 space-y-1.5"
            data-testid={`scenario-multiradar-panel-${scenario.id}`}
        >
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-wider text-[#4FB6C6]">
                    Multi-radar gating
                </span>
                <span className="text-[10px] font-mono text-[#5A6B7C] tabular-nums">
                    {radars.length} radars
                </span>
            </div>
            <ul className="space-y-1">
                {radars.map((r, i) => {
                    const name = r?.name || r?.id || `radar_${i + 1}`;
                    const rng = r?.detection_range_m;
                    const alt = r?.detection_ceiling_m;
                    return (
                        <li
                            key={r?.id || name}
                            className="flex items-center gap-2 text-[11px]"
                            data-testid={`scenario-multiradar-row-${scenario.id}-${i}`}
                        >
                            <span
                                className="inline-block h-1.5 w-1.5 rounded-full bg-[#4FB6C6]"
                                aria-hidden="true"
                            />
                            <span className="font-medium">{name}</span>
                            <span className="text-[#5A6B7C] tabular-nums">
                                {rng != null
                                    ? `${(Number(rng) / 1000).toFixed(1)} km`
                                    : "—"}
                                {" · "}
                                {alt != null
                                    ? `${(Number(alt)).toLocaleString()} m`
                                    : "—"}
                            </span>
                        </li>
                    );
                })}
            </ul>
            <p
                className="text-[10px] text-[#5A6B7C] italic pt-0.5"
                data-testid={`scenario-multiradar-runtime-note-${scenario.id}`}
            >
                Runtime track-handoff visualisation appears once a campaign is
                executing on this scenario.
            </p>
        </div>
    );
}


function AttachmentChips({ scenario }) {
    // CP-G9 P1-9 — bind ``s`` from the summary block and derive
    // ``airframe_count`` from ``airframe_ids[]`` on the live response.
    // Prior code dereferenced an unbound ``s`` here, throwing the
    // "Cannot read properties of undefined (reading 'airframe_count')"
    // crash that surfaced the regression during user-journey capture.
    const s = scenario?.summary || {};
    const airframeCount = scenario?.airframe_ids?.length ?? 0;
    const chips = [
        { k: "airframes", v: `${airframeCount} airframe${airframeCount === 1 ? "" : "s"}` },
        { k: "threats", v: `${s.threat_count ?? 0} threat${s.threat_count === 1 ? "" : "s"}` },
        { k: "env", v: s.has_environment ? "env" : "no env" },
        { k: "guidance", v: s.has_guidance_profile ? "guidance" : "no guidance" },
        { k: "engagement", v: s.has_engagement ? "engagement" : "no engagement" },
    ];
    return (
        <div className="flex flex-wrap gap-1" data-testid={`scenario-chips-${scenario.id}`}>
            {chips.map((c) => {
                const on = !c.v.startsWith("no ") && !c.v.startsWith("0 ");
                return (
                    <span
                        key={c.k}
                        className={
                            "text-[10px] font-mono px-1.5 py-0.5 border rounded " +
                            (on
                                ? "bg-ux-bg-2 text-ux-text-1 border-ux-border-1"
                                : "bg-transparent text-ux-text-3 border-dashed border-ux-border-1")
                        }
                    >
                        {c.v}
                    </span>
                );
            })}
        </div>
    );
}

function matchesValidation(scenario, filter) {
    if (!filter) return true;
    const s = scenario?.summary || {};
    const blocking = scenario?.validation?.blocking_count ?? 0;
    const runnable =
        blocking === 0 &&
        // CP-G9 P1-9 — derive airframe count from ``airframe_ids[]``.
        (scenario?.airframe_ids?.length ?? 0) > 0 &&
        s.has_environment &&
        s.threat_count > 0 &&
        s.has_guidance_profile &&
        s.has_engagement;
    if (filter === "runnable") return runnable;
    if (filter === "blocked") return blocking > 0;
    if (filter === "draft") return !runnable && blocking === 0;
    return true;
}

// --------------------------------------------------------------------
// Page
// --------------------------------------------------------------------
export default function ScenariosLibraryPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [creating, setCreating] = useState(false);
    const [creatingArchetype, setCreatingArchetype] = useState(null);
    const [cloningId, setCloningId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [filters, setFilters] = useState({
        scope: "tenant",
        validation: "",
        q: "",
    });
    const navigate = useNavigate();
    const { user } = useAuth();

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const rows = await sim.scenarios.list({ scope: filters.scope });
            setItems(Array.isArray(rows) ? rows : []);
        } catch (e) {
            setErr(formatApiError(e, "Failed to load scenarios"));
        } finally {
            setLoading(false);
        }
    }, [filters.scope]);

    useEffect(() => {
        load();
    }, [load]);

    const visible = useMemo(() => {
        const q = filters.q.trim().toLowerCase();
        return items.filter((s) => {
            if (!matchesValidation(s, filters.validation)) return false;
            if (!q) return true;
            const hay = [s.name, s.notes, s.description].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [items, filters.validation, filters.q]);

    const handleNewScenario = async () => {
        setCreating(true);
        try {
            const doc = await sim.scenarios.create({
                name: "Untitled scenario",
                notes: "",
            });
            navigate(`/workbench/${doc.id}#section=airframe`);
        } catch (e) {
            toast.error(formatApiError(e, "Could not create scenario"));
        } finally {
            setCreating(false);
        }
    };

    const handleNewFromArchetype = async (archetypeId) => {
        // Phase 20 CP3.b — one-click factory. POSTs to
        // ``/api/sim/scenarios/from-archetype/{archetype_id}``; on 201
        // we land in the battlespace editor with the persisted
        // scenario loaded. 422 errors (unknown archetype, schema
        // violation) surface as a toast.
        setCreatingArchetype(archetypeId);
        try {
            const doc = await sim.scenarios.createFromArchetype(archetypeId);
            navigate(`/battlespace?scenarioId=${encodeURIComponent(doc.id)}`);
        } catch (e) {
            toast.error(formatApiError(e, "Could not create scenario from archetype"));
        } finally {
            setCreatingArchetype(null);
        }
    };

    const handleOpen = (s) => {
        navigate(`/workbench/${s.id}#section=airframe`);
    };

    const handleClone = async (s) => {
        setCloningId(s.id);
        try {
            // Phase 6 — single atomic POST replaces the previous
            // client-side create+attach* shim. The server deep-copies
            // every slot reference (airframes, environment, threats,
            // guidance, engagement, terrain, obstacles), the parameter
            // tree, tags + provenance, and re-runs the validation gate.
            const doc = await sim.scenarios.clone(s.id, {
                name: `Copy of ${s.name}`,
            });
            toast.success(`Cloned "${s.name}"`);
            navigate(`/workbench/${doc.id}#section=airframe`);
        } catch (e) {
            toast.error(formatApiError(e, "Clone failed"));
        } finally {
            setCloningId(null);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        setDeletingId(confirmDelete.id);
        try {
            await sim.scenarios.remove(confirmDelete.id);
            toast.success(`Deleted "${confirmDelete.name}"`);
            setItems((prev) => prev.filter((x) => x.id !== confirmDelete.id));
        } catch (e) {
            presetImmutabilityToast(e, {
                entityType: "scenarios",
                id: confirmDelete.id,
                navigate,
            });
        } finally {
            setDeletingId(null);
            setConfirmDelete(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="scenarios-library-page">
            <PageHeader
                title="Scenarios"
                subtitle="The library of Workbench scenarios you can open, clone, or extend."
                actions={
                    <div className="flex gap-2">
                        <SpinnerButton
                            size="sm"
                            loading={creating}
                            onClick={handleNewScenario}
                            testId="scenarios-new"
                        >
                            <Plus className="h-3.5 w-3.5 mr-1" /> New scenario
                        </SpinnerButton>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={creatingArchetype !== null}
                                    data-testid="scenarios-new-from-archetype"
                                >
                                    {creatingArchetype !== null
                                        ? "Creating…"
                                        : "New from archetype"}
                                    <ChevronDown className="h-3.5 w-3.5 ml-1" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                data-testid="archetype-menu"
                            >
                                <DropdownMenuLabel>
                                    Customer archetypes
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {CUSTOMER_ARCHETYPES.map((a) => (
                                    <DropdownMenuItem
                                        key={a.id}
                                        onSelect={() => handleNewFromArchetype(a.id)}
                                        data-testid={`archetype-option-${a.id}`}
                                    >
                                        {a.label}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                }
            />

            {/* Filters row */}
            <div
                className="flex flex-wrap items-center gap-2 border border-ux-border-1 rounded-md p-2 bg-ux-bg-1"
                data-testid="scenarios-filters"
            >
                <Filter className="w-4 h-4 text-ux-text-3" />
                <Select
                    value={filters.scope}
                    onValueChange={(v) => setFilters((f) => ({ ...f, scope: v }))}
                >
                    <SelectTrigger
                        className="w-[200px] bg-ux-bg-2 text-[12px] font-mono"
                        data-testid="filter-scope"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                        {SCOPES.map((s) => (
                            <SelectItem key={s.id} value={s.id} className="text-[12px] font-mono">
                                {s.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={filters.validation || "__all__"}
                    onValueChange={(v) =>
                        setFilters((f) => ({ ...f, validation: v === "__all__" ? "" : v }))
                    }
                >
                    <SelectTrigger
                        className="w-[180px] bg-ux-bg-2 text-[12px] font-mono"
                        data-testid="filter-validation"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                        {VALIDATIONS.map((v) => (
                            <SelectItem
                                key={v.id || "__all__"}
                                value={v.id || "__all__"}
                                className="text-[12px] font-mono"
                            >
                                {v.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <input
                    type="search"
                    value={filters.q}
                    onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                    placeholder="Search name or notes…"
                    className="flex-1 min-w-[200px] bg-ux-bg-2 border border-ux-border-1 rounded px-2 py-1 text-[12px] font-mono text-ux-text-1 placeholder:text-ux-text-3"
                    data-testid="filter-search"
                />
                <span
                    className="ml-auto text-[11px] font-mono text-ux-text-3"
                    data-testid="scenarios-count"
                >
                    {loading
                        ? "loading…"
                        : `${visible.length} scenario${visible.length === 1 ? "" : "s"}`}
                </span>
            </div>

            {err && (
                <div
                    className="text-ux-danger text-[13px]"
                    role="alert"
                    data-testid="scenarios-error"
                >
                    {err}
                </div>
            )}

            {!loading && visible.length === 0 && (
                <EmptyState
                    title="No scenarios yet"
                    subtitle="Start by clicking New scenario, or switch the scope filter to see the bundled preset scenarios."
                    actionLabel="New scenario"
                    onAction={handleNewScenario}
                    actionTestId="scenarios-empty-new"
                    image="/empty-trajectory.png"
                    testId="scenarios-empty"
                />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {visible.map((s) => (
                    <div
                        key={s.id}
                        className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 flex flex-col gap-2"
                        data-testid={`scenario-card-${s.id}`}
                    >
                        <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                                <h3
                                    className="text-[14px] font-medium text-ux-text-1 truncate"
                                    data-testid={`scenario-name-${s.id}`}
                                >
                                    {s.name || s.id}
                                </h3>
                                <p className="text-[11px] font-mono text-ux-text-3 truncate">
                                    {s.updated_at
                                        ? `updated ${new Date(s.updated_at).toISOString().slice(0, 16).replace("T", " ")}`
                                        : "—"}
                                </p>
                            </div>
                            <OwnerBadge scenario={s} />
                        </div>
                        <div className="flex items-center gap-2">
                            <ValidationPill scenario={s} />
                            <MultiAgentBadge scenario={s} />
                        </div>
                        <p className="text-[12px] text-ux-text-2 line-clamp-2 min-h-[32px]">
                            {s.notes || s.description || "—"}
                        </p>
                        <AttachmentChips scenario={s} />
                        <MultiRadarPanel scenario={s} />
                        <div className="flex flex-wrap gap-2 pt-1">
                            <Button
                                size="sm"
                                onClick={() => handleOpen(s)}
                                data-testid={`scenario-open-${s.id}`}
                            >
                                <PlayCircle className="h-3.5 w-3.5 mr-1" /> Open in Workbench
                            </Button>
                            {isMultiAgentScenario(s) && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                        navigate(`/battlespace?scenarioId=${encodeURIComponent(s.id)}`)
                                    }
                                    data-testid={`scenario-open-battlespace-${s.id}`}
                                >
                                    <Globe className="h-3.5 w-3.5 mr-1" /> Open in battlespace editor
                                </Button>
                            )}
                            <SpinnerButton
                                size="sm"
                                variant="outline"
                                loading={cloningId === s.id}
                                onClick={() => handleClone(s)}
                                testId={`scenario-clone-${s.id}`}
                            >
                                <Copy className="h-3.5 w-3.5 mr-1" /> Clone
                            </SpinnerButton>
                            <PresetPublishButton
                                entityType="scenarios"
                                entityId={s.id}
                                entity={s}
                                user={user}
                                size="sm"
                                variant="outline"
                                onPublished={load}
                                testId={`scenario-publish-${s.id}`}
                            />
                            {!s.is_preset && !s.summary?.is_preset && (
                                <SpinnerButton
                                    size="sm"
                                    variant="outline"
                                    loading={deletingId === s.id}
                                    onClick={() => setConfirmDelete(s)}
                                    testId={`scenario-delete-${s.id}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                                </SpinnerButton>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(o) => !o && setConfirmDelete(null)}
            >
                <AlertDialogContent data-testid="scenario-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this scenario?</AlertDialogTitle>
                        <AlertDialogDescription>
                            &quot;{confirmDelete?.name}&quot; will be removed from your tenant.
                            This cannot be undone. Attached library items (airframes, threats,
                            environments…) are not touched.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="scenario-delete-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            data-testid="scenario-delete-confirm-btn"
                        >
                            Delete scenario
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <div
                className="text-[11px] font-mono text-ux-text-3 flex items-center gap-1 pt-2"
                data-testid="scenarios-library-footer"
            >
                <FlaskConical className="w-3 h-3" />
                Reference data (airframes, threats, environments, guidance, engagement)
                now lives in the per-asset libraries under PLAN.
            </div>
        </div>
    );
}
